import { BellOff } from "lucide-react";

/**
 * SOUND-618 — internal-only opt-in to suppress the merchant-facing notification
 * on an application status change (or a MID comment / pend mutation).
 *
 * Shared by the registration Actions panel (`ApplicationTab`) and the MID
 * application status panel (`MidApplication`) so the control cannot drift between
 * the two surfaces. Both send the resulting boolean as `suppressNotifications` on
 * the request body; the API gates it on an internal caller regardless.
 *
 * Deliberately NOT sticky: the caller resets it to false after every successful
 * action. A checkbox that silently stays ticked is how a merchant quietly stops
 * being told anything.
 *
 * Renders nothing for non-internal users — belt and braces alongside the API-side
 * internal-only gate, since a client must never see a lever it cannot pull.
 */
export default function SuppressNotificationsToggle({
  checked,
  onChange,
  disabled = false,
  visible = true,
  className = "",
  // Copy is surface-specific: "merchant" reads wrong on an internal-only note.
  label = "Don't notify the merchant",
  hint = "Records the change without sending the email, Telegram message, or partner webhook. Use for backfills and data cleanup.",
}) {
  if (!visible) return null;

  return (
    <div
      className={`rounded-lg border px-3 py-2.5 transition-colors ${
        checked ? "border-warning/40 bg-warning/10" : "border-border bg-muted/30"
      } ${className}`}
    >
      <label className="flex items-start gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-border text-brand focus:ring-brand/40 disabled:opacity-50"
        />
        <span className="min-w-0">
          <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
            {checked && <BellOff className="w-3.5 h-3.5 text-warning shrink-0" />}
            {label}
          </span>
          <span className="mt-0.5 block text-xs text-muted-foreground">{hint}</span>
        </span>
      </label>
    </div>
  );
}
